import { Box, Grid, Link, Skeleton, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import {
  InboxSidebarStatusMap,
  OutreachContactsContext,
} from "contexts/OutreachContacts";
import { forwardRef, useContext } from "react";
import {
  Route,
  Link as RouterLink,
  Routes,
  useNavigate,
} from "react-router-dom";
import { OutreachContact } from "schemas/dashboard";
import { routes } from "schemas/routes";

import OutreachContactDrawer from "../../OutreachContactDrawer";
import { useOutreachTable } from "../../useOutreachTable";
import ContactRow from "../ContactRow";
import styles from "../styles";

const REF_INDEX = 5;

const EmailList = forwardRef<HTMLDivElement, {}>((_props, ref) => {
  const { isSmallScreen } = useOutreachTable();
  const { setAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const {
    contacts,
    fetchLoading,
    currentSidebarOption,
    isFiltering,
    fetchContactsParams,
    handleFetchContacts,
    fetchMoreLoading,
  } = useContext(OutreachContactsContext);

  const clearFilters = () => {
    const updatedParams = { ...fetchContactsParams };
    updatedParams.page = 1;
    updatedParams.selectedSuggestion = null;
    updatedParams.importantOnly = false;
    handleFetchContacts(updatedParams);
  };

  const openSent = () => {
    const updatedParams = { ...fetchContactsParams };
    updatedParams.page = 1;
    updatedParams.selectedSuggestion = null;
    updatedParams.importantOnly = false;
    updatedParams.statuses = InboxSidebarStatusMap.sent;
    handleFetchContacts(updatedParams);
    navigate(`/${routes.inboxSent}`);
  };

  return (
    <>
      {!fetchLoading && contacts && contacts.length === 0 && (
        <Box sx={styles.noContacts}>
          <Typography component="div" variant="h4" gutterBottom>
            {isFiltering
              ? `No contacts found`
              : `Empty ${currentSidebarOption?.label.toLowerCase()} folder`}
          </Typography>
          {isFiltering ? (
            <>
              <Typography component="div" paragraph variant="h6">
                Your filters are hiding your results, try{" "}
                <Link sx={{ cursor: "pointer" }} onClick={clearFilters}>
                  clearing filters
                </Link>
                .
              </Typography>
            </>
          ) : (
            <>
              {currentSidebarOption?.key !== "sent" ? (
                <>
                  <Typography component="div" paragraph variant="h6">
                    To see all of your sent emails,{" "}
                    <Link sx={{ cursor: "pointer" }} onClick={openSent}>
                      open your sent folder
                    </Link>
                    .
                  </Typography>
                </>
              ) : (
                <>
                  <Typography component="div" paragraph variant="h6">
                    Send emails in the{" "}
                    <RouterLink to={`/${routes.brands}`}>brands tab</RouterLink>{" "}
                    and monitor their status in this tab.
                  </Typography>
                </>
              )}
            </>
          )}
        </Box>
      )}

      {contacts?.length > 0 && !isSmallScreen && (
        <Typography
          variant="h6"
          gutterBottom
          sx={{ my: { xs: 0, md: 1.5 }, ml: 2 }}
        >
          {currentSidebarOption?.label} Emails
        </Typography>
      )}

      {contacts &&
        contacts.length > 0 &&
        contacts.map((row: OutreachContact, idx: number) => (
          <Grid
            item
            xs={12}
            key={row?.id}
            ref={
              (
                contacts.length > REF_INDEX
                  ? idx === contacts.length - REF_INDEX
                  : idx === contacts.length - 1
              )
                ? ref
                : undefined
            }
          >
            <ContactRow contact={row} setAlert={setAlert} />
          </Grid>
        ))}

      {(fetchLoading || fetchMoreLoading) && (
        <Box sx={{ p: 2 }}>
          <Skeleton animation="wave" width="20%" height="40px" sx={{ mb: 4 }} />

          {Array.from(new Array(7)).map((_, index) => (
            <Skeleton
              key={index}
              animation="wave"
              width="100%"
              height="150px"
              sx={{ mt: -6 }}
            />
          ))}
        </Box>
      )}

      <Routes>
        <Route
          path=":outreachContactId/*"
          element={<OutreachContactDrawer />}
        />
      </Routes>
    </>
  );
});

export default EmailList;
